import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, URLS, SG_SURV_ITM_LIST, SF_SURV_ITM_INFO, SG_SURV_ITM_LANG_LIST } from "./services/PtliSurvItmMgmtService";
import { PTLI0418001Q } from "../qustnr/PTLI0418001Q";
import { PTLI0419001Q } from "../ques/PTLI0419001Q";
import { PTLI0418005Q } from "../qustnr/PTLI0418005Q";
import { PTLI0419005Q } from "../ques/PTLI0419005Q";

/*
 * Survey Item Registration
 * !== 설문 항목 등록 ==!
 */

export const PTLI0420002S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const langCodes = ["KO", "EN", "TZ"];

    useResource({
        defaultSchema: [],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survItmInfo: useForm({
            defaultSchema: SF_SURV_ITM_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survItmLangList: useGrid({
            defaultSchema: SG_SURV_ITM_LANG_LIST
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        createSurvItm: useFetch({
            api: (data) => APIS.createSurvItm({ ...data, survItmLangList: grid.survItmLangList.getData() }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        navigate(URLS.ptli0420001q);
                    },
                    onCancel: () => {
                        navigate(URLS.ptli0420001q);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.survItmInfo.setErrors(error.response.data.errors);
                }
                modal.openModal({
                    content: "msg.00006",
                });
            },
        }),

        getNextItmOrd: useFetch({
            api: ({ quesId }: { quesId: string }) => APIS.getNextItmOrd(quesId),
            onSuccess: (nextOrd: number) => {
                form.survItmInfo.setValue("itmOrd", String(nextOrd));
            },
            onError: () => {
                toast.showToast({ type: "warning", content: "msg.00006" });
            },
        }),
    };
    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        createSurvItm: () => {
            if (!comnUtils.isEmpty(form.survItmInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.survItmInfo.handleSubmit(
                () => {
                grid.survItmLangList.clearData();

                langCodes.forEach((langCd) => {
                const itmCn = form.survItmInfo.getValue(`itmCn${langCd}`); // itmCnEN/TZ/KO
                if (!comnUtils.isEmpty(itmCn)) {
                    grid.survItmLangList.addRow({
                    langCd,
                    itmCn , 
                    });
                }
                });
                if (grid.survItmLangList.getData().length === 0) {
                    modal.openModal({ content: "error occurs" /* 최소 1개 언어 입력 요청 */ });
                    return;
                }

                const data = form.survItmInfo.getValues();

                modal.openModal({
                    content: "msg.00101", // 저장 하시겠습니까?
                    onConfirm: () => fetch.createSurvItm.fetch(data),
                    close: false,
                });
            },
            () => {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
            },
            )();
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        //form.survItmInfo.setEditable("qustnrTtle", false);
        //form.survItmInfo.setEditable("quesCn", false);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control
                                {...form.survItmInfo.schema.qustnrTtle}
                                readOnly="true"
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const modalCallback = (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survItmInfo.setValue("qustnrTtle", item.qustnrTtle);
                                                form.survItmInfo.setValue("qestnrId", item.qestnrId);
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "md",
                                            content: <PTLI0418005Q callback={modalCallback} />,
                                        });
                                    },
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.survItmInfo.schema.quesCn}
                                readOnly={true}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const qestnrId = form.survItmInfo.watch("qestnrId"); // 설문 선택에서 세팅된 값
                                        if (!qestnrId) {
                                            toast.showToast({ type: "warning", content: "msg.ptl.00835" }); // Please select a Questionnaire to begin.
                                            return;
                                        }
                                        const modalCallback = async (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survItmInfo.setValue("quesCn", item.quesCn);
                                                form.survItmInfo.setValue("quesId", item.quesId);

                                                try {
                                                    const { data } = await APIS.getNextItmOrd(item.quesId); // ← axios 응답
                                                    form.survItmInfo.setValue("itmOrd", String(data.nextOrd)); // ← 문자열로
                                                } catch (e) {
                                                    toast.showToast({ type: "warning", content: "msg.00006" });
                                                }
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "lg",
                                            content: <PTLI0419005Q qestnrId={qestnrId} callback={modalCallback} />,
                                        });
                                    },
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survItmInfo.schema.itmOrd} readOnly={true} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survItmInfo.schema.itmCnTZ} />
                            <Group.Control {...form.survItmInfo.schema.itmCnEN} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survItmInfo.schema.itmCnKO} />
                        </Group.Row>
                    </Group.Section>
                    <Group.Section hidden={true}>
                            {" "}
                            <Grid {...grid.survItmLangList.grid} />
                    </Group.Section>

                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Button
                        role={"list"}
                        onClick={() => {
                            navigate(URLS.ptli0420001q);
                        }}
                    />
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role={"save"}
                        onClick={() => {
                            handler.createSurvItm();
                        }}
                    />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
